/* Where a team has eyes, and how far each pair sees.

   Every square a team looks out from — the cells it holds, the cells its troops stand on (a camp
   on open ground, a garrison lent to an ally) and the ground its marches are on — each with the
   reach of its *own owner's* research (U3, 2026-09-07): a teammate's city sees as far as the
   teammate's thermal suits, not the viewer's. A square several allies stand on takes the longest
   reach among them. Mirrors the server's `compute_visibility._vision_sources`.

   Its reader is the fog (`utils/fog.js`), which walks the ring around each lookout to paint and
   redact. It had a second, the spy check, until spying was removed (owner, 2026-10-26, 36/X1). */

const line = (userMetadata) => (userMetadata?.team === "zombie" ? "stalker" : "thermal_suits")

/* How far a player passively sees: the base radius plus their thermal-suits / stalker line.
   Mirrors the server's `modifiers.detection_radius`. */
export const detectionRadius = (userMetadata, rules) =>
  (rules?.DETECTION_RADIUS ?? 0) + Number(userMetadata?.researches?.[line(userMetadata)] ?? 0)

export const parseKey = (key) => {
  const [col, row] = key.split("-").map(Number)
  return { col, row }
}

export const hasUnits = (units) => Object.values(units || {}).some((unit) => (unit?.number ?? 0) > 0)

/* The ids on `team`'s side, as strings — user ids arrive as numbers on the payload and as strings
   in `metadata_by_user`'s keys, so every comparison here is on the string. */
export const alliesOf = (users, viewerId) => {
  const team = users?.[String(viewerId)]?.team
  if (!team) return new Set()
  return new Set(
    Object.entries(users)
      .filter(([, user]) => user?.team === team)
      .map(([id]) => String(id))
  )
}

/* `[{col, row, radius}]`, one per square the side looks out from. `metadata` is the game's raw
   metadata (`cells`, `users`, `movements`); `allies` a set of string ids. */
export const teamLookouts = (metadata, allies, rules) => {
  const users = metadata?.users || {}
  const sources = new Map()
  const lookOutFrom = (key, ownerId) => {
    const radius = detectionRadius(users[String(ownerId)], rules)
    sources.set(key, Math.max(radius, sources.get(key) ?? -1))
  }
  Object.entries(metadata?.cells || {}).forEach(([key, cell]) => {
    if (cell?.owner != null && allies.has(String(cell.owner))) lookOutFrom(key, cell.owner)
    Object.entries(cell?.metadata_by_user || {}).forEach(([id, entry]) => {
      if (allies.has(String(id)) && hasUnits(entry?.units)) lookOutFrom(key, id)
    })
  })
  ;(metadata?.movements || []).forEach((movement) => {
    if (!allies.has(String(movement.user_id))) return
    if (!hasUnits(movement.units)) return
    const position = movement.path?.[movement.current_index ?? 0]
    if (position) lookOutFrom(`${position[0]}-${position[1]}`, movement.user_id)
  })
  return [...sources.entries()].map(([key, radius]) => ({ ...parseKey(key), radius }))
}
